import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';
import {
  ArrowLeft,
  Search,
  Download,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  History,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Activity,
  Calendar,
  CheckCircle2,
  Clock,
  XCircle,
  HelpCircle,
} from 'lucide-react';
import HeadersAuth from '../../components/base/HeadersAuth';
import { useAuthContext } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value) => {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
    timeZoneName: 'short',
  });
};

const formatPnl = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (n > 0) return `+$${abs}`;
  if (n < 0) return `-$${abs}`;
  return `$${abs}`;
};

const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
};

const formatExitReason = (reason) => {
  if (!reason) return null;
  const r = String(reason).toLowerCase();
  if (r === 'tp' || r.includes('profit')) return 'Take Profit';
  if (r === 'sl' || r.includes('stop')) return 'Stop Loss';
  if (r === 'auto') return 'Auto Exit';
  if (r === 'manual') return 'Manual Exit';
  return reason;
};

const isCompletedTrade = (data) =>
  data.tradeType === 'completed' ||
  data.lifecycle?.phase === 'completed' ||
  Boolean(data.lifecycle?.exit && data.lifecycle?.entry) ||
  Boolean(data.exit?.filledAt);

const downloadCsv = (filename, headers, rows) => {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [headers.map((h) => escape(h.label)).join(',')].concat(
    rows.map((r) => headers.map((h) => escape(h.accessor(r))).join(','))
  );
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const TradeHistoryPage = () => {
  const { user } = useAuthContext();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [symbolFilter, setSymbolFilter] = useState('ALL');
  const [expandedId, setExpandedId] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const userId = user?.uid;

  useEffect(() => {
    if (!userId || !db) {
      setTrades([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    // Stream the user's trades from tbl_user_order_backlogs
    const q = query(
      collection(db, 'tbl_user_order_backlogs'),
      where('userId', '==', userId),
      limit(2000)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          const isCompleted = isCompletedTrade(data);

          const entryFilled =
            toDate(data.entry?.filledAt) ||
            toDate(data.entryFilledAt) ||
            toDate(data.entry?.placedAt) ||
            toDate(data.createdAt);

          const exitFilled =
            toDate(data.exit?.filledAt) ||
            toDate(data.exitFilledAt) ||
            toDate(data.updatedAt);

          const netPnl = Number(data.performance?.netPnl ?? data.netPnl ?? 0);
          const grossPnl = Number(data.performance?.grossPnl ?? 0);
          const roi = data.performance?.roi != null ? Number(data.performance.roi) : null;

          const tradeType = data.tradeType || (isCompleted ? 'completed' : 'entry_only');

          // Extract option specifics if present
          const option = data.entry?.option || data.exit?.option || {};
          const optionSymbol =
            option.optionSymbol ||
            data.optionSymbol ||
            (option.legs && option.legs[0]?.optionSymbol) ||
            '';
          const strike = option.strike || (option.legs && option.legs[0]?.strike) || null;
          const putCall = option.putCall || (option.legs && option.legs[0]?.putCall) || null;
          const expiry = option.expiry || (option.legs && option.legs[0]?.expiry) || null;

          return {
            id: docSnap.id,
            symbol: data.symbol || data.underlying || '—',
            broker: data.broker || data.brokerType || '—',
            accountId: data.accountId || data.account || '—',
            tradeType,
            isCompleted,
            side: data.entry?.side || data.side || 'BUY',
            quantity: data.entry?.quantity || data.quantity || data.size || 1,
            entryAt: entryFilled,
            exitAt: exitFilled,
            entryPrice: data.performance?.entryPrice ?? data.entry?.price ?? null,
            exitPrice: data.performance?.exitPrice ?? data.exit?.price ?? null,
            exitReason: data.exit?.reason || data.exitReason || null,
            netPnl,
            grossPnl,
            roi,
            holdingPeriod: data.performance?.holdingPeriod || null,
            optionSymbol,
            strike,
            putCall,
            expiry,
            groupId: data.group_id || data.groupId || '—',
            raw: data,
          };
        });

        // Sort descending: latest trades first
        rows.sort((a, b) => {
          const aTime = (a.exitAt?.getTime() || a.entryAt?.getTime() || 0);
          const bTime = (b.exitAt?.getTime() || b.entryAt?.getTime() || 0);
          return bTime - aTime;
        });

        setTrades(rows);
        setLoading(false);
        setLastUpdatedAt(new Date());
      },
      (err) => {
        console.error('Error streaming user trade history:', err);
        setError('Failed to load trade history. Please try refreshing.');
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [userId]);

  // Derived unique symbols for dropdown filter
  const availableSymbols = useMemo(() => {
    const set = new Set();
    trades.forEach((t) => {
      if (t.symbol && t.symbol !== '—') set.add(t.symbol);
    });
    return Array.from(set).sort();
  }, [trades]);

  // Filtered rows
  const filteredTrades = useMemo(() => {
    return trades.filter((t) => {
      // Symbol filter
      if (symbolFilter !== 'ALL' && t.symbol !== symbolFilter) {
        return false;
      }
      // Status filter
      if (statusFilter === 'COMPLETED' && !t.isCompleted) {
        return false;
      }
      if (statusFilter === 'OPEN' && (t.isCompleted || t.tradeType === 'cancelled')) {
        return false;
      }
      if (statusFilter === 'CANCELLED' && t.tradeType !== 'cancelled') {
        return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const haystack = `${t.symbol} ${t.broker} ${t.accountId} ${t.optionSymbol} ${t.exitReason || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      return true;
    });
  }, [trades, symbolFilter, statusFilter, searchQuery]);

  // KPI calculations (based on completed trades)
  const kpis = useMemo(() => {
    let completedCount = 0;
    let realizedPnl = 0;
    let winCount = 0;
    let lossCount = 0;

    trades.forEach((t) => {
      if (t.isCompleted) {
        completedCount++;
        realizedPnl += t.netPnl;
        if (t.netPnl > 0) winCount++;
        else if (t.netPnl < 0) lossCount++;
      }
    });

    const winRate = completedCount > 0 ? (winCount / completedCount) * 100 : 0;
    const avgTrade = completedCount > 0 ? realizedPnl / completedCount : 0;

    return {
      total: trades.length,
      completed: completedCount,
      realizedPnl,
      winCount,
      lossCount,
      winRate,
      avgTrade,
    };
  }, [trades]);

  const handleExportCsv = () => {
    const headers = [
      { label: 'Trade ID', accessor: (r) => r.id },
      { label: 'Symbol', accessor: (r) => r.symbol },
      { label: 'Status', accessor: (r) => r.tradeType },
      { label: 'Side', accessor: (r) => r.side },
      { label: 'Quantity', accessor: (r) => r.quantity },
      { label: 'Entry Date (ET)', accessor: (r) => formatDateTime(r.entryAt) },
      { label: 'Entry Price', accessor: (r) => (r.entryPrice != null ? r.entryPrice : '') },
      { label: 'Exit Date (ET)', accessor: (r) => formatDateTime(r.exitAt) },
      { label: 'Exit Price', accessor: (r) => (r.exitPrice != null ? r.exitPrice : '') },
      { label: 'Exit Reason', accessor: (r) => formatExitReason(r.exitReason) || '' },
      { label: 'Net P&L ($)', accessor: (r) => r.netPnl.toFixed(2) },
      { label: 'ROI (%)', accessor: (r) => (r.roi != null ? `${r.roi.toFixed(2)}%` : '') },
      { label: 'Broker', accessor: (r) => r.broker },
      { label: 'Account ID', accessor: (r) => r.accountId },
      { label: 'Group ID', accessor: (r) => r.groupId },
    ];
    downloadCsv(`trade-history-${new Date().toISOString().slice(0, 10)}.csv`, headers, filteredTrades);
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="min-h-screen bg-[#0b0d2a] text-white flex flex-col font-sans">
      <HeadersAuth />

      <main className="flex-1 pt-[96px] pb-16 px-4 md:px-8 max-w-7xl mx-auto w-full space-y-6">
        {/* Navigation & Title Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
          <div className="space-y-1">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1.5 text-sm text-purple-400 hover:text-purple-300 transition-colors font-medium mb-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white flex items-center gap-2.5">
                <History className="w-7 h-7 text-purple-400" />
                My Trade History
              </h1>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-950/60 border border-emerald-500/40 text-emerald-400">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Live
              </span>
            </div>
            <p className="text-sm text-gray-400">
              Live automated execution ledger from Quantum Flo Auto Algo.
              {lastUpdatedAt && ` Last updated: ${lastUpdatedAt.toLocaleTimeString()}`}
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={handleExportCsv}
              disabled={filteredTrades.length === 0}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-gray-800/80 hover:bg-gray-700/80 border border-gray-700 text-gray-200 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4 text-cyan-400" />
              Export CSV
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
          {/* Realized P&L */}
          <div className="bg-gray-900/70 border border-gray-800 rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between text-xs text-gray-400 font-medium mb-1.5">
              <span>Total Realized P&amp;L</span>
              {kpis.realizedPnl >= 0 ? (
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-400" />
              )}
            </div>
            <div
              className={`text-xl md:text-2xl font-bold tabular-nums ${
                kpis.realizedPnl > 0
                  ? 'text-emerald-400'
                  : kpis.realizedPnl < 0
                    ? 'text-red-400'
                    : 'text-gray-300'
              }`}
            >
              {loading ? '...' : formatPnl(kpis.realizedPnl)}
            </div>
            <div className="text-[11px] text-gray-500 mt-1">Across all completed trades</div>
          </div>

          {/* Completed Trades */}
          <div className="bg-gray-900/70 border border-gray-800 rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between text-xs text-gray-400 font-medium mb-1.5">
              <span>Completed Trades</span>
              <CheckCircle2 className="w-4 h-4 text-purple-400" />
            </div>
            <div className="text-xl md:text-2xl font-bold tabular-nums text-white">
              {loading ? '...' : kpis.completed.toLocaleString()}
            </div>
            <div className="text-[11px] text-gray-500 mt-1">
              Out of {kpis.total.toLocaleString()} total logged
            </div>
          </div>

          {/* Win Rate */}
          <div className="bg-gray-900/70 border border-gray-800 rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between text-xs text-gray-400 font-medium mb-1.5">
              <span>Win Rate</span>
              <Activity className="w-4 h-4 text-cyan-400" />
            </div>
            <div className="text-xl md:text-2xl font-bold tabular-nums text-cyan-400">
              {loading ? '...' : `${kpis.winRate.toFixed(1)}%`}
            </div>
            <div className="text-[11px] text-gray-500 mt-1">
              {kpis.winCount} wins · {kpis.lossCount} losses
            </div>
          </div>

          {/* Average Trade */}
          <div className="bg-gray-900/70 border border-gray-800 rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between text-xs text-gray-400 font-medium mb-1.5">
              <span>Avg. P&amp;L / Trade</span>
              <DollarSign className="w-4 h-4 text-purple-400" />
            </div>
            <div
              className={`text-xl md:text-2xl font-bold tabular-nums ${
                kpis.avgTrade > 0
                  ? 'text-emerald-400'
                  : kpis.avgTrade < 0
                    ? 'text-red-400'
                    : 'text-gray-300'
              }`}
            >
              {loading ? '...' : formatPnl(kpis.avgTrade)}
            </div>
            <div className="text-[11px] text-gray-500 mt-1">Per completed trade</div>
          </div>
        </div>

        {/* Filter and Search Bar */}
        <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-3 shadow-md">
          <div className="flex flex-wrap items-center gap-2.5 flex-1 min-w-[280px]">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search symbol, broker, account..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 bg-gray-800/80 border border-gray-700/80 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
            </div>

            {/* Symbol Filter */}
            <select
              value={symbolFilter}
              onChange={(e) => setSymbolFilter(e.target.value)}
              className="bg-gray-800/80 border border-gray-700/80 rounded-lg px-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-purple-500"
            >
              <option value="ALL">All Symbols</option>
              {availableSymbols.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-gray-800/80 border border-gray-700/80 rounded-lg px-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-purple-500"
            >
              <option value="ALL">All Statuses</option>
              <option value="COMPLETED">Completed</option>
              <option value="OPEN">Open (Entry Only)</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div className="text-xs text-gray-400">
            Showing <span className="font-semibold text-white">{filteredTrades.length}</span> of{' '}
            <span className="font-semibold text-white">{trades.length}</span> trades
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="p-4 rounded-xl bg-red-950/40 border border-red-700/50 text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Trade Ledger Table */}
        <div className="bg-gray-900/70 border border-gray-800 rounded-xl overflow-hidden shadow-xl">
          {loading ? (
            <div className="p-16 text-center text-gray-400 space-y-3">
              <RefreshCw className="w-7 h-7 animate-spin mx-auto text-purple-400" />
              <p className="text-sm">Loading your trade history from Firestore...</p>
            </div>
          ) : filteredTrades.length === 0 ? (
            <div className="p-16 text-center text-gray-500 space-y-3">
              <History className="w-10 h-10 mx-auto text-gray-600" />
              <div className="text-base font-medium text-gray-300">No trades found</div>
              <p className="text-sm max-w-md mx-auto text-gray-400">
                {searchQuery || symbolFilter !== 'ALL' || statusFilter !== 'ALL'
                  ? 'No trades matched your active filters. Try clearing search or changing filters.'
                  : 'Automated trades placed by Quantum Flo Auto Algo will appear here automatically.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-950/80 border-b border-gray-800 text-xs text-gray-400 uppercase tracking-wider">
                  <tr>
                    <th className="py-3 px-4 font-semibold">Date &amp; Time (ET)</th>
                    <th className="py-3 px-4 font-semibold">Symbol</th>
                    <th className="py-3 px-4 font-semibold">Side &amp; Qty</th>
                    <th className="py-3 px-4 font-semibold">Price (Entry → Exit)</th>
                    <th className="py-3 px-4 font-semibold">Status</th>
                    <th className="py-3 px-4 font-semibold text-right">Net P&amp;L</th>
                    <th className="py-3 px-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/80">
                  {filteredTrades.map((trade) => {
                    const isExpanded = expandedId === trade.id;
                    const pnl = Number(trade.netPnl) || 0;
                    const hasOption = Boolean(trade.strike || trade.putCall || trade.optionSymbol);

                    return (
                      <React.Fragment key={trade.id}>
                        <tr
                          onClick={() => toggleExpand(trade.id)}
                          className="hover:bg-gray-800/40 cursor-pointer transition-colors group"
                        >
                          {/* Date / Time */}
                          <td className="py-3.5 px-4 whitespace-nowrap">
                            <div className="font-medium text-gray-200">
                              {formatDateTime(trade.exitAt || trade.entryAt)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {trade.exitAt && trade.entryAt
                                ? `In: ${formatDateTime(trade.entryAt)}`
                                : trade.exitReason
                                  ? formatExitReason(trade.exitReason)
                                  : trade.broker}
                            </div>
                          </td>

                          {/* Symbol & Option details */}
                          <td className="py-3.5 px-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-base text-white tracking-wide">
                                {trade.symbol}
                              </span>
                              {hasOption && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-purple-950/60 text-purple-300 border border-purple-800/60">
                                  {trade.strike ? `$${trade.strike}` : ''}{' '}
                                  {trade.putCall === 'C'
                                    ? 'CALL'
                                    : trade.putCall === 'P'
                                      ? 'PUT'
                                      : ''}
                                </span>
                              )}
                            </div>
                            {trade.expiry && (
                              <div className="text-xs text-gray-500">Exp: {trade.expiry}</div>
                            )}
                          </td>

                          {/* Side & Qty */}
                          <td className="py-3.5 px-4 whitespace-nowrap">
                            <div className="font-medium text-gray-200">
                              <span
                                className={`font-semibold ${
                                  trade.side?.toUpperCase().includes('BUY')
                                    ? 'text-cyan-400'
                                    : 'text-amber-400'
                                }`}
                              >
                                {trade.side}
                              </span>
                              <span className="text-gray-400 ml-1.5">× {trade.quantity}</span>
                            </div>
                            <div className="text-xs text-gray-500 capitalize">{trade.broker}</div>
                          </td>

                          {/* Price */}
                          <td className="py-3.5 px-4 whitespace-nowrap">
                            <div className="font-medium text-gray-200">
                              {trade.entryPrice != null ? formatMoney(trade.entryPrice) : '—'}
                              <span className="text-gray-500 mx-1.5">→</span>
                              {trade.exitPrice != null ? formatMoney(trade.exitPrice) : '—'}
                            </div>
                            {trade.roi != null && (
                              <div
                                className={`text-xs font-medium ${
                                  trade.roi >= 0 ? 'text-emerald-400' : 'text-red-400'
                                }`}
                              >
                                ROI: {trade.roi >= 0 ? '+' : ''}
                                {trade.roi.toFixed(1)}%
                              </div>
                            )}
                          </td>

                          {/* Status */}
                          <td className="py-3.5 px-4 whitespace-nowrap">
                            {trade.isCompleted ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-950/60 text-emerald-300 border border-emerald-700/50">
                                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                                Completed
                              </span>
                            ) : trade.tradeType === 'cancelled' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-800 text-gray-400 border border-gray-700">
                                <XCircle className="w-3 h-3 text-gray-400" />
                                Cancelled
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-950/60 text-blue-300 border border-blue-700/50">
                                <Clock className="w-3 h-3 text-blue-400" />
                                Open
                              </span>
                            )}
                          </td>

                          {/* Net PnL */}
                          <td className="py-3.5 px-4 whitespace-nowrap text-right font-semibold tabular-nums text-base">
                            <span
                              className={
                                pnl > 0
                                  ? 'text-emerald-400'
                                  : pnl < 0
                                    ? 'text-red-400'
                                    : 'text-gray-400'
                              }
                            >
                              {trade.isCompleted ? formatPnl(pnl) : '—'}
                            </span>
                          </td>

                          {/* Expand chevron */}
                          <td className="py-3.5 px-2 text-right text-gray-500 group-hover:text-gray-300">
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 ml-auto" />
                            ) : (
                              <ChevronDown className="w-4 h-4 ml-auto" />
                            )}
                          </td>
                        </tr>

                        {/* Expanded detail row */}
                        {isExpanded && (
                          <tr className="bg-gray-950/50 border-b border-gray-800">
                            <td colSpan={7} className="p-4 text-xs text-gray-300 space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 bg-gray-900/80 rounded-lg p-3.5 border border-gray-800">
                                <div>
                                  <div className="text-gray-500 font-medium mb-0.5">Entry Filled</div>
                                  <div className="text-gray-200 font-mono">
                                    {formatDateTime(trade.entryAt)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-500 font-medium mb-0.5">Exit Filled</div>
                                  <div className="text-gray-200 font-mono">
                                    {formatDateTime(trade.exitAt)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-500 font-medium mb-0.5">Exit Reason</div>
                                  <div className="text-gray-200 font-medium">
                                    {formatExitReason(trade.exitReason) || 'Standard close'}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-500 font-medium mb-0.5">Holding Period</div>
                                  <div className="text-gray-200 font-medium">
                                    {trade.holdingPeriod || '—'}
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-gray-900/80 rounded-lg p-3.5 border border-gray-800">
                                <div>
                                  <div className="text-gray-500 font-medium mb-0.5">Account ID</div>
                                  <div className="text-gray-200 font-mono">{trade.accountId}</div>
                                </div>
                                <div>
                                  <div className="text-gray-500 font-medium mb-0.5">Broker</div>
                                  <div className="text-gray-200 capitalize">{trade.broker}</div>
                                </div>
                                <div>
                                  <div className="text-gray-500 font-medium mb-0.5">Option Contract</div>
                                  <div className="text-gray-200 font-mono truncate" title={trade.optionSymbol}>
                                    {trade.optionSymbol || 'Standard'}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TradeHistoryPage;
